import {Component, Inject} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {forkJoin} from 'rxjs';
import {take} from 'rxjs/operators';
import {DateUtil} from '../../../../common/util/DateUtil';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {ReferenceService} from '../../../services/reference.service';
import {ApplicationSchemeDTO} from '../../../models/application/ApplicationSchemeDTO';
import {ApplicationSchemeHistoryDTO} from '../../../models/application/ApplicationSchemeHistoryDTO';

enum Controls {
  Scheme = 'scheme',
  MembershipNumber = 'membershipNumber',
  AssociationCode = 'associationCode',
  StartDate = 'startDate',
  EndDate = 'endDate'
}

@Component({
  selector: 'app-scheme-history-add-dialog',
  templateUrl: './scheme-history-add-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class SchemeHistoryAddDialogComponent {
  readonly controls = Controls;
  readonly dateFormat = DateUtil.dateFormat;

  heading: string;
  schemeHistoryForm: FormGroup;
  schemes: ReferenceDTO[];
  associations: ReferenceDTO[];

  constructor(
    private formBuilder: FormBuilder,
    private referenceService: ReferenceService,
    public dialogRef: MatDialogRef<SchemeHistoryAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      applicationPersonId: number,
      schemeHistory?: ApplicationSchemeHistoryDTO
    }
  ) {
    this.heading = data.schemeHistory ? 'Edit Scheme History' : 'Add Scheme History';
    this.initForm();
    this.initLookUpValues();
  }

  onSave(): void {
    if (this.schemeHistoryForm.valid) {
      const formValue = this.schemeHistoryForm.value;
      const selectedScheme: ReferenceDTO = formValue[Controls.Scheme];

      const schemeHistory: ApplicationSchemeHistoryDTO = {
        ...this.data.schemeHistory,
        scheme: {
          schemeCode: selectedScheme.key,
          schemeName: selectedScheme.value
        } as ApplicationSchemeDTO,
        membershipNumber: formValue[Controls.MembershipNumber],
        associationCode: formValue[Controls.AssociationCode],
        startDate: formValue[Controls.StartDate],
        endDate: formValue[Controls.EndDate]
      } as ApplicationSchemeHistoryDTO;

      this.dialogRef.close(schemeHistory);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private initForm(): void {
    this.schemeHistoryForm = this.formBuilder.group({
      [Controls.Scheme]: [null, Validators.required],
      [Controls.MembershipNumber]: ['', Validators.required],
      [Controls.AssociationCode]: [''],
      [Controls.StartDate]: [null, Validators.required],
      [Controls.EndDate]: [null]
    });
  }

  private initLookUpValues(): void {
    forkJoin([
      this.referenceService.getAllByFieldName('schemeCode'),
      this.referenceService.getAllByFieldName('associationCode')
    ]).pipe(take(1))
      .subscribe({
        next: (results: ReferenceDTO[][]) => {
          this.schemes = results[0];
          this.associations = results[1];

          if (this.data.schemeHistory && this.schemes?.length) {
            this.setFormValues(this.data.schemeHistory);
          }
        },
      });
  }

  private setFormValues(schemeHistory: ApplicationSchemeHistoryDTO): void {
    const matchingScheme: ReferenceDTO = this.schemes?.find(scheme =>
      scheme.key === schemeHistory.scheme?.schemeCode
    );

    this.schemeHistoryForm.patchValue({
      [Controls.Scheme]: matchingScheme || null,
      [Controls.MembershipNumber]: schemeHistory.membershipNumber,
      [Controls.AssociationCode]: schemeHistory.associationCode,
      [Controls.StartDate]: schemeHistory.startDate ? new Date(schemeHistory.startDate) : null,
      [Controls.EndDate]: schemeHistory.endDate ? new Date(schemeHistory.endDate) : null
    });
  }
}
